import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useBannerOffset } from "@hooks/useBannerOffset";

import instance from "@store/middleware/api";

import { bannerSurface } from "@utils/bannerGradient";

import { useAuth } from "@/hooks/auth";

/**
 * Site-wide non-dismissible banner shown when the user has a pending
 * channel-reducing downgrade with no keep-list confirmed yet. Companion
 * to PendingDowngradeGlobalModal — the modal is now dismissable (X close
 * button), so this banner persists across page navigation as the
 * always-visible reminder that claim processing is paused until the
 * user picks.
 *
 * Clicking "Open picker" dispatches `pending-downgrade-open-picker`,
 * which PendingDowngradeGlobalModal listens for to clear its session-
 * dismissed flag and reopen the modal.
 *
 * Refetches on auth + route change so the banner clears the moment the
 * user confirms the keep-list (pendingDowngradeKeepChannelIds becomes
 * non-empty) or backs out via Keep Current Plan.
 */
const DisconnectChannelsReminderBanner: React.FC = () => {
  const { pathname } = useLocation();
  const { myData } = useAuth();
  const userId = (myData as any)?.id ?? (myData as any)?._id ?? null;
  const [show, setShow] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const evaluate = async () => {
      if (!userId) {
        if (!cancelled) setShow(false);
        return;
      }
      try {
        const subResp = await instance.get("/subscriptions/my");
        const sub: any = subResp?.data;
        if (!sub) { if (!cancelled) setShow(false); return; }
        const chResp = await instance.get("/user-channels");
        const items: any[] = chResp?.data?.channels || [];
        // Mirrors PendingDowngradeGlobalModal: only fires for POST-rollover
        // over-quota state (downgrade already applied + claims paused).
        // Pre-rollover scheduled downgrades are managed via Manage Add-ons
        // in-page UI — no site-wide banner needed.
        const isOverQuota = items.length > sub.channels;
        if (!cancelled) setShow(isOverQuota);
      } catch {
        if (!cancelled) setShow(false);
      }
    };
    void evaluate();
    const handler = () => { void evaluate(); };
    const onFocus = () => { void evaluate(); };
    window.addEventListener("pending-downgrade-changed", handler);
    window.addEventListener("focus", onFocus);
    const pollId = window.setInterval(() => { void evaluate(); }, 60_000);
    return () => {
      cancelled = true;
      window.removeEventListener("pending-downgrade-changed", handler);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(pollId);
    };
  }, [userId, pathname]);

  // Publish the banner's rendered height so the body's padding-top reserves
  // its strip — same mechanism every other global banner uses (e.g.
  // PastDueGlobalBanner). This banner lives inside the FIXED SecondHeader
  // container on every page including /account/*, so it must always reserve
  // its height; otherwise page content (and the in-page downgrade banner)
  // slides up behind it on the claims/account pages.
  useBannerOffset("--disconnect-reminder-banner-h", show, bannerRef);

  if (!show) return null;

  const openPicker = () => {
    window.dispatchEvent(new Event("pending-downgrade-open-picker"));
  };

  return (
    <div
      data-banner-surface
      ref={bannerRef}
      data-global-banner
      style={{
        width: "100%",
        marginTop: -1,
        ...bannerSurface("rgb(217, 119, 6)"),
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        color: "#fff",
        fontFamily: '"Raleway", sans-serif',
        fontSize: "0.875rem",
      }}
    >
      <span style={{ fontWeight: 500 }}>
        Claim processing is paused due to channel downgrade. Choose which channels to disconnect.
      </span>
      <button
        onClick={openPicker}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 14px",
          borderRadius: 6,
          background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
          border: "none",
          color: "#2a1d14",
          fontFamily: '"Raleway", sans-serif',
          fontSize: "0.8125rem",
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
          boxShadow: "0 0 12px rgba(186, 144, 92, 0.25)",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(186, 144, 92, 0.5)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 0 12px rgba(186, 144, 92, 0.25)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
        </svg>
        Open picker
      </button>
    </div>
  );
};

export default DisconnectChannelsReminderBanner;
